import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import { detectChannel } from '../utils/auth';
import { PhoneFormat } from '../utils/components';
import {
    emailOrPhoneSchema,
    passwordSchema,
    emailWithPassword,
    phone,
    forgotPassword,
} from '../api';
import { Heading, TextLink } from './Typography';
import { MessageCard } from './MessageCard';
import { PasswordField } from './Fields';
import { OneTimePassword } from './OneTimePassword';
import { useSetTokensWithRedirect } from '../hooks/useSetTokensWithRedirect';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
// import { pushDL } from '@lendica/api';
import { useAppContainerContext } from './AppContainer';

type Path = 'email' | 'phone';

export const EmailOrPhoneAuth = ({ onAuthSuccess }: { onAuthSuccess?: (tokens?: any) => void }) => {
    const [step, setStep] = useState<1 | 2>(1);
    const [path, setPath] = useState<Path | null>(null);
    const { loginValue, updateLoginValue, resetLoginValue, setError, setPageLoading } =
        useAppContainerContext();
    const navigate = useNavigate();

    const setTokensWithRedirect = useSetTokensWithRedirect(onAuthSuccess);

    const onNavBack = () => {
        setStep(1);
        setPath(null);
        setError(null);
    };

    const onNavForward = () => {
        setStep(2);
    };

    const handleStep1Submit = async (
        { emailOrPhone }: { emailOrPhone: string },
        { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
    ) => {
        setError(null);
        try {
            setSubmitting(true);
            const det = detectChannel(emailOrPhone);
            if (!det) return;
            if (det.channel === 'email') {
                setPath('email');
                updateLoginValue({ email: det.normalized });
                onNavForward();
            } else {
                setPath('phone');
                updateLoginValue({ phone: det.normalized });
                await phone(det.normalized);
                onNavForward();
            }
        } catch (error: any) {
            setError(error?.message || 'Invalid email or phone number');
            setTimeout(() => {
                setError(null);
            }, 2500);
        } finally {
            setSubmitting(false);
        }
    };

    const handleStep2Submit = async (
        { email, password }: { email: string; password: string },
        { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
    ) => {
        try {
            setSubmitting(true);
            updateLoginValue({ email, password });
            const tokens = await emailWithPassword(email, password);
            setTokensWithRedirect(tokens);
        } catch (error) {
            setError('Invalid email or password');
            setTimeout(() => {
                setError(null);
            }, 2500);
        } finally {
            setSubmitting(false);
        }
    };
    const handleForgotPassword = async () => {
        try {
            setPageLoading(true);
            const data = await forgotPassword(loginValue.email || '');
            setTimeout(() => {
                navigate('reset');
                setPageLoading(false);
            }, 0);
        } catch (error) {
            setError('Failed to send reset email. Please try again.');
            setPageLoading(false);
        }
    };

    return (
        <>
            {step === 1 && <EmailOrPhone handleSubmit={handleStep1Submit} />}
            {step === 2 && path === 'email' && (
                <EmailAndPassword
                    email={loginValue.email || ''}
                    handleSubmit={handleStep2Submit}
                    onNavBack={onNavBack}
                    handleForgotPassword={handleForgotPassword}
                />
            )}
            {step === 2 && path === 'phone' && (
                <OneTimePassword
                    phone={loginValue.phone || ''}
                    setTokens={setTokensWithRedirect}
                    onNavBack={onNavBack}
                />
            )}
            {/* <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 4,
                }}
            >
                <Typography variant="caption" color="text.secondary">
                    By continuing, you agree to our Terms and Privacy Policy.
                </Typography>
            </Box> */}
        </>
    );
};

const EmailOrPhone = ({
    handleSubmit,
}: {
    handleSubmit: (
        { emailOrPhone }: { emailOrPhone: string },
        { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
    ) => void;
}) => {
    const navigate = useNavigate();
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                width: '100%',
                gap: 2,
            }}
        >
            <Heading>Welcome back</Heading>
            <MessageCard>Use the email or phone you registered with to sign in.</MessageCard>
            <Formik
                initialValues={{
                    emailOrPhone: '',
                }}
                validationSchema={emailOrPhoneSchema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                    <Box component={Form} sx={{ width: '100%' }}>
                        <Stack spacing={2.5} sx={{ width: '100%' }}>
                            {(() => {
                                const trimmed = (values.emailOrPhone || '').trim();
                                const isPhoneLike =
                                    !!trimmed && !trimmed.includes('@') && /^[+\d(]/.test(trimmed);
                                return (
                                    <TextField
                                        name="emailOrPhone"
                                        label="Work email or phone"
                                        placeholder="name@company.com or +1 555 123 4567"
                                        value={values.emailOrPhone}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.emailOrPhone && !!errors.emailOrPhone}
                                        helperText={
                                            (touched.emailOrPhone && errors.emailOrPhone) || ' '
                                        }
                                        fullWidth
                                        autoFocus={true}
                                        autoComplete={isPhoneLike ? 'tel' : 'username'}
                                        inputMode={isPhoneLike ? 'tel' : 'email'}
                                        InputProps={
                                            isPhoneLike
                                                ? { inputComponent: PhoneFormat }
                                                : undefined
                                        }
                                    />
                                );
                            })()}

                            <LoadingButton
                                type="submit"
                                variant="contained"
                                disabled={isSubmitting}
                                loading={isSubmitting}
                                fullWidth
                                size="large"
                            >
                                Next
                            </LoadingButton>
                            <Stack sx={{ width: '100%', justifyContent: 'center' }}>
                                <Typography variant="body1" sx={{ textAlign: 'center' }}>
                                    Don't have an account?{' '}
                                    <TextLink onClick={() => navigate('/signup')}>Sign up</TextLink>
                                </Typography>
                            </Stack>
                        </Stack>
                    </Box>
                )}
            </Formik>
        </Box>
    );
};

export const EmailAndPassword = ({
    email,
    handleForgotPassword,
    handleSubmit,
    onNavBack,
    includeSignUpLink = true,
}: {
    email: string | undefined;
    handleForgotPassword: () => void;
    handleSubmit: (
        { email, password }: { email: string; password: string },
        { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
    ) => void;
    onNavBack?: () => void;
    includeSignUpLink?: boolean;
}) => {
    const navigate = useNavigate();
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                flex: 1,
                width: '100%',
                gap: 2,
            }}
        >
            {onNavBack && (
                <IconButton
                    size="small"
                    onClick={() => {
                        // Track back navigation
                        // pushDL('auth_step', {
                        //     step: 'password_back_to_email_or_phone',
                        //     phone: phone,
                        // });
                        onNavBack();
                    }}
                    sx={{ ml: -0.5, mb: 1 }}
                >
                    <ArrowBackIosIcon />
                </IconButton>
            )}
            <Heading>Enter your password</Heading>
            <MessageCard>Enter the password for this email.</MessageCard>
            <Formik
                initialValues={{
                    email: email || '',
                    password: '',
                }}
                validationSchema={passwordSchema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                    <Box
                        component={Form}
                        sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5 }}
                    >
                        <Stack sx={{ width: '100%', mb: 2.5 }}>
                            <TextField
                                name="email"
                                label="Work email"
                                value={values.email}
                                helperText={' '}
                                fullWidth
                                disabled={!!email}
                            />
                            <PasswordField
                                name="password"
                                label="Password"
                                value={values.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.password && !!errors.password}
                                helperText={(touched.password && errors.password) || ' '}
                                fullWidth
                                autoComplete="current-password"
                            />
                            <Box
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <TextLink onClick={handleForgotPassword}>
                                    Don't know your password?
                                </TextLink>
                            </Box>
                        </Stack>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            loading={isSubmitting}
                            fullWidth
                            size="large"
                        >
                            Sign in
                        </LoadingButton>
                        {includeSignUpLink && (
                            <Stack sx={{ width: '100%', justifyContent: 'center' }}>
                                <Typography variant="body1" sx={{ textAlign: 'center' }}>
                                    Don't have an account?{' '}
                                    <TextLink onClick={() => navigate('/signup')}>Sign up</TextLink>
                                </Typography>
                            </Stack>
                        )}
                    </Box>
                )}
            </Formik>
        </Box>
    );
};
